import {
  Monitor, ChefHat, ClipboardList, Calculator, Brain, Settings,
  UtensilsCrossed, ShoppingCart, QrCode, Truck, Package, Tag,
  FileText, MessageCircle, Headphones, Wand2, Megaphone,
  Building2, Crown, Users, Activity, Lightbulb, BarChart3, Shield, Gauge
} from 'lucide-react';

// Category group → kitchen label mapping
const CATEGORY_KITCHEN_LABELS = {
  fast_food: 'modules.kitchen',
  saudi_arabic: 'modules.kitchen',
  international: 'modules.kitchen',
  beverages_desserts: 'modules.prepStation',
  modern_service: 'modules.kitchen',
};

// Get prep label for any business type based on its category group
export function getPrepLabel(businessType, categoryGroup) {
  return CATEGORY_KITCHEN_LABELS[categoryGroup] || 'modules.kitchen';
}

// Permission required to access each module/page
export const MODULE_PERMISSIONS = {
  pos: 'pos.access',
  preparation: 'kitchen.access',
  'operations/inventory': 'inventory.view',
  'operations/menu': 'menu.view',
  'operations/orders': 'orders.view',
  'operations/tables': 'tables.view',
  'operations/fleet': 'fleet.view',
  'finance/smart-suggestions': 'accounting.dashboard',
  'finance/order-analytics': 'accounting.dashboard',
  'finance/accounting': 'accounting.dashboard',
  'finance/invoices': 'invoices.view',
  'ai/agents': 'ai.tools',
  'ai/conversational': 'ai.tools',
  'ai/takeover': 'ai.takeover',
  'ai/builder': 'ai.tools',
  'settings/branches': 'branches.view',
  'settings/subscription': 'subscription.manage',
  'settings/team': 'team.manage',
};

export const MODULES = {
  pos: {
    key: 'pos',
    icon: Monitor,
    labelKey: 'modules.pos',
    descKey: 'modules.posDesc',
    path: '/dashboard/pos',
    color: 'from-emerald-500 to-green-600',
    bgColor: 'bg-emerald-500/10',
    textColor: 'text-emerald-600 dark:text-emerald-400',
    fullscreen: true,
    alwaysVisible: true,
    permission: 'pos.access',
  },
  preparation: {
    key: 'preparation',
    icon: ChefHat,
    labelKey: 'modules.preparation',
    descKey: 'modules.preparationDesc',
    path: '/dashboard/kitchen',
    color: 'from-orange-500 to-amber-600',
    bgColor: 'bg-orange-500/10',
    textColor: 'text-orange-600 dark:text-orange-400',
    fullscreen: true,
    alwaysVisible: true,
    permission: 'kitchen.access',
  },
  operations: {
    key: 'operations',
    icon: ClipboardList,
    labelKey: 'modules.operations',
    descKey: 'modules.operationsDesc',
    path: '/dashboard/operations',
    defaultPath: '/dashboard/operations/menu',
    color: 'from-blue-500 to-indigo-600',
    bgColor: 'bg-blue-500/10',
    textColor: 'text-blue-600 dark:text-blue-400',
    items: [
      { path: '/dashboard/operations/menu', labelKey: 'nav.menu', icon: UtensilsCrossed, alwaysVisible: true, permission: 'menu.view' },
      { path: '/dashboard/operations/inventory', labelKey: 'nav.inventory', icon: Package, alwaysVisible: true, permission: 'menu.view' },
      { path: '/dashboard/operations/offers', labelKey: 'nav.offers', icon: Tag, alwaysVisible: true, permission: 'menu.view' },
      { path: '/dashboard/operations/orders', labelKey: 'nav.orders', icon: ShoppingCart, alwaysVisible: true, permission: 'orders.view' },
      { path: '/dashboard/operations/tables', labelKey: 'nav.tables', icon: QrCode, permission: 'tables.view' },
      { path: '/dashboard/operations/fleet', labelKey: 'nav.fleet', icon: Truck, permission: 'fleet.view' },
    ],
  },
  finance: {
    key: 'finance',
    icon: Calculator,
    labelKey: 'modules.finance',
    descKey: 'modules.financeDesc',
    path: '/dashboard/finance',
    defaultPath: '/dashboard/finance/flash-report',
    color: 'from-amber-500 to-orange-600',
    bgColor: 'bg-amber-500/10',
    textColor: 'text-amber-600 dark:text-amber-400',
    hasAlerts: true,
    items: [
      { path: '/dashboard/finance/flash-report', labelKey: 'nav.flashReport', icon: FileText, alwaysVisible: true, permission: 'accounting.dashboard' },
      { path: '/dashboard/finance/accounting', labelKey: 'nav.accounting', icon: Calculator, alwaysVisible: true, permission: 'accounting.dashboard' },
      { path: '/dashboard/finance/cash-waste', labelKey: 'nav.cashWaste', icon: Calculator, alwaysVisible: true, permission: 'accounting.dashboard' },
      { path: '/dashboard/finance/planning', labelKey: 'nav.planning', icon: Calculator, alwaysVisible: true, permission: 'accounting.dashboard' },
      { path: '/dashboard/finance/vendors', labelKey: 'nav.vendors', icon: Calculator, alwaysVisible: true, permission: 'accounting.dashboard' },
      { path: '/dashboard/finance/invoices', labelKey: 'nav.invoices', icon: FileText, alwaysVisible: true, permission: 'invoices.view' },
      { path: '/dashboard/finance/order-analytics', labelKey: 'nav.orderAnalytics', icon: BarChart3, alwaysVisible: true, permission: 'accounting.dashboard' },
      { path: '/dashboard/finance/smart-suggestions', labelKey: 'nav.smartSuggestions', icon: Lightbulb, alwaysVisible: true, permission: 'accounting.dashboard' },
      { path: '/dashboard/finance/reports', labelKey: 'nav.reports', icon: BarChart3, alwaysVisible: true, permission: 'accounting.dashboard' },
      { path: '/dashboard/finance/ai-advisor', labelKey: 'nav.aiAdvisor', icon: Brain, alwaysVisible: true, permission: 'accounting.dashboard' },
    ],
  },
  ai: {
    key: 'ai',
    icon: Brain,
    labelKey: 'modules.ai',
    descKey: 'modules.aiDesc',
    path: '/dashboard/ai',
    defaultPath: '/dashboard/ai/agents',
    color: 'from-purple-500 to-violet-600',
    bgColor: 'bg-purple-500/10',
    textColor: 'text-purple-600 dark:text-purple-400',
    items: [
      { path: '/dashboard/ai/agents', labelKey: 'nav.agents', icon: Brain, alwaysVisible: true, permission: 'ai.tools' },
      { path: '/dashboard/ai/conversational', labelKey: 'nav.conversational', icon: MessageCircle, alwaysVisible: true, permission: 'ai.tools' },
      { path: '/dashboard/ai/takeover', labelKey: 'nav.takeover', icon: Headphones, alwaysVisible: true, permission: 'ai.takeover' },
      { path: '/dashboard/ai/builder', labelKey: 'nav.builder', icon: Wand2, alwaysVisible: true, permission: 'ai.tools' },
      { path: '/dashboard/ai/campaigns', labelKey: 'nav.campaigns', icon: Megaphone, alwaysVisible: true, permission: 'ai.tools' },
      { path: '/dashboard/ai/usage', labelKey: 'nav.aiUsage', icon: Gauge, alwaysVisible: true, permission: 'ai.tools' },
      { path: '/dashboard/ai/voice-agent', labelKey: 'nav.voiceAgent', icon: Headphones, alwaysVisible: true, permission: 'ai.tools' },
    ],
  },
  settings: {
    key: 'settings',
    icon: Settings,
    labelKey: 'modules.settings',
    descKey: 'modules.settingsDesc',
    path: '/dashboard/settings',
    defaultPath: '/dashboard/settings/branches',
    color: 'from-slate-500 to-gray-600',
    bgColor: 'bg-slate-500/10',
    textColor: 'text-slate-600 dark:text-slate-400',
    items: [
      { path: '/dashboard/settings/branches', labelKey: 'nav.branches', icon: Building2, alwaysVisible: true, permission: 'branches.view' },
      { path: '/dashboard/settings/subscription', labelKey: 'nav.subscription', icon: Crown, alwaysVisible: true, permission: 'subscription.manage' },
      { path: '/dashboard/settings/team', labelKey: 'nav.team', icon: Users, alwaysVisible: true, permission: 'team.manage' },
      { path: '/dashboard/settings/activity-log', labelKey: 'nav.activityLog', icon: Activity, alwaysVisible: true, permission: 'team.manage' },
      { path: '/dashboard/settings/my-data', labelKey: 'nav.myData', icon: Shield, alwaysVisible: true },
    ],
  },
};

export function getVisibleModules(businessType = 'restaurant', hasPermission = () => true) {
  return Object.values(MODULES)
    .filter(mod => {
      if (mod.permission && !hasPermission(mod.permission)) return false;
      if (mod.items) {
        const visibleItems = mod.items.filter(item => {
          const bizVisible = !item.visibleFor || item.visibleFor.includes(businessType);
          const permVisible = !item.permission || hasPermission(item.permission);
          return bizVisible && permVisible;
        });
        if (visibleItems.length === 0) return false;
      }
      return true;
    })
    .map(mod => {
      if (!mod.items) return mod;
      return {
        ...mod,
        items: mod.items.filter(item => {
          const bizVisible = !item.visibleFor || item.visibleFor.includes(businessType);
          const permVisible = !item.permission || hasPermission(item.permission);
          return bizVisible && permVisible;
        }),
      };
    });
}

export function getModuleByPath(pathname) {
  for (const mod of Object.values(MODULES)) {
    if (mod.fullscreen && pathname === mod.path) return mod;
    if (mod.items && mod.items.some(item => pathname === item.path)) return mod;
  }
  return null;
}

export function getModuleKey(pathname) {
  const mod = getModuleByPath(pathname);
  return mod?.key || null;
}
